import { useState, useEffect } from 'react';
import axios from 'axios';
import { X, Plus, Trash2, Edit, Upload, FileText, Star, Heart, AlertTriangle, User, Shield, MapPin, Crop, ChevronDown } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import LocationMap from './LocationMap';
import { compressImageFile } from '../utils/imageCompress';
import ImageCropper from './ImageCropper';
import { idadeDoAnimal } from '../utils/age';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BEHAVIOR_COLORS = { verde: '#4CAF50', amarelo: '#FFC107', vermelho: '#F44336' };
const VACCINE_OPTIONS = ['Múltipla Cães', 'Raiva', 'Múltipla+Raiva', 'Tripla Gatos', 'Puppy'];
const TEST_TYPES = ['FIV e FELV', 'Teste de Quatro', 'Parvo Corona', 'Leishmaniose', 'Ecografia Abdominal', 'Ecocardiograma', 'Raio-X', 'Outros'];
// Types that only need a free-text observation + date (no positive/negative sub-results)
const OBS_ONLY_TESTS = new Set(['Ecografia Abdominal', 'Ecocardiograma', 'Raio-X', 'Outros']);
const DEWORMING_PRODUCTS = ['Bravecto', 'Zipyran', 'Bravecto+Zipyran', 'FrontLine', 'Stronghold', 'Outra'];
const DEATH_TYPES = [{ v: 'eutanasia', l: 'Eutanásia' }, { v: 'morte_natural', l: 'Morte Natural' }, { v: 'eutanasia_leishmaniose', l: 'Eutanásia por Leishmaniose' }];
const placeholderImg = '/sem-foto.jpg';

// Resolves a stored photo/file URL. Cloudinary URLs already start with http(s)://
// and must NOT be prefixed with the backend host. Legacy paths like "/api/uploads/..."
// or "data:" base64 are returned as-is/prefixed appropriately.
const resolveUrl = (u) => {
  if (!u) return '';
  if (typeof u !== 'string') return '';
  if (u.startsWith('http://') || u.startsWith('https://') || u.startsWith('data:')) return u;
  return `${process.env.REACT_APP_BACKEND_URL}${u}`;
};

// Date input that displays/accepts dd/mm/yyyy but stores yyyy-mm-dd
function DateInput({ value, onChange, className = '' }) {
  const toDisplay = (iso) => {
    if (!iso) return '';
    const p = iso.slice(0, 10).split('-');
    return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : iso;
  };
  const toIso = (display) => {
    const p = display.replace(/[^\d/]/g, '').split('/');
    if (p.length === 3 && p[0].length === 2 && p[1].length === 2 && p[2].length === 4) {
      return `${p[2]}-${p[1]}-${p[0]}`;
    }
    return '';
  };
  const [text, setText] = useState(toDisplay(value));
  const [focused, setFocused] = useState(false);
  useEffect(() => { if (!focused) setText(toDisplay(value)); }, [value, focused]);

  const handleChange = (e) => {
    let v = e.target.value.replace(/[^\d/]/g, '');
    // Auto-add slashes
    if (v.length === 2 && !v.includes('/')) v += '/';
    if (v.length === 5 && v.split('/').length === 2) v += '/';
    if (v.length > 10) v = v.slice(0, 10);
    setText(v);
    const iso = toIso(v);
    if (iso) onChange(iso);
    else if (v === '') onChange('');
  };

  const isoForPicker = value && value.length >= 10 ? value.slice(0, 10) : '';
  return (
    <div className={`relative inline-flex items-center w-full ${className}`}>
      <input value={text} onChange={handleChange}
        onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
        placeholder="dd/mm/aaaa" maxLength={10}
        className="flex-1 rounded-lg border border-border bg-background px-2 py-1.5 pr-8 text-sm" />
      <input type="date" value={isoForPicker}
        onChange={e => { if (e.target.value) { onChange(e.target.value); setText(toDisplay(e.target.value)); } }}
        className="absolute right-1 top-1/2 -translate-y-1/2 w-6 h-6 opacity-0 cursor-pointer"
        aria-label="Selecionar data no calendário" />
      <svg className="absolute right-1.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none"
        fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    </div>
  );
}

const fmtDate = (d) => {
  if (!d) return 'N/D';
  const p = d.slice(0, 10).split('-');
  return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : d;
};

const daysSince = (d) => {
  if (!d) return null;
  const diff = Math.floor((Date.now() - new Date(d)) / 86400000);
  return diff >= 0 ? diff : null;
};

export default function AnimalDetail({ animal: animalProp, onClose, onUpdate, onEdit }) {
  const { user: authUser } = useAuth();
  const canDelete = authUser?.role === 'tecnico' || authUser?.role === 'admin';
  // Local refreshable copy of animal so photos/files/fields update live without re-mounting.
  const [animal, setAnimal] = useState(animalProp);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setAnimal(animalProp); }, [animalProp?.id]);
  const refreshAnimal = async () => {
    if (!animalProp?.id) return;
    try {
      const r = await axios.get(`${API}/animais/${animalProp.id}`);
      setAnimal(r.data);
    } catch { /* ignore */ }
  };
  // Combine: refresh local animal AND notify parent
  const handleUpdate = async () => {
    await refreshAnimal();
    onUpdate?.();
  };
  const [tab, setTab] = useState('info');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const [cropperFiles, setCropperFiles] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteText, setDeleteText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [editingPhotoIndex, setEditingPhotoIndex] = useState(null);
  const [vacinas, setVacinas] = useState([]);
  const [testes, setTestes] = useState([]);
  const [tratamentos, setTratamentos] = useState([]);
  const [desps, setDesps] = useState([]);
  const [obs, setObs] = useState(animal.observacoes || '');
  const [obsAdmin, setObsAdmin] = useState(animal.obs_administrativa || '');
  const [showExit, setShowExit] = useState(null);
  const [exitData, setExitData] = useState({});
  const [showAddVacina, setShowAddVacina] = useState(false);
  const [showAddTeste, setShowAddTeste] = useState(false);
  const [showAddTrat, setShowAddTrat] = useState(false);
  const [showAddDesp, setShowAddDesp] = useState(false);
  const [newVacina, setNewVacina] = useState({ nome: VACCINE_OPTIONS[0], data: '', proxima_data: '', observacao: '' });
  const [editVacinaId, setEditVacinaId] = useState(null);
  const [editVacina, setEditVacina] = useState({ nome: '', data: '', proxima_data: '', observacao: '' });
  const [newTeste, setNewTeste] = useState({ tipo: TEST_TYPES[0], sub_resultados: {}, data: '', observacao: '' });
  const [newTrat, setNewTrat] = useState({ nome: '', descricao: '', protocolo: '', data_inicio: '', data_fim: '' });
  const [editTratId, setEditTratId] = useState(null);
  const [editTrat, setEditTrat] = useState({ nome: '', descricao: '', protocolo: '', data_inicio: '', data_fim: '' });
  const [newDesp, setNewDesp] = useState({ produto: DEWORMING_PRODUCTS[0], data: '', produto_outro: '', observacao: '' });
  const [editDespId, setEditDespId] = useState(null);
  const [editDesp, setEditDesp] = useState({ produto: DEWORMING_PRODUCTS[0], data: '', produto_outro: '', observacao: '' });
  const [dispAdocao, setDispAdocao] = useState(animal.disponivel_adocao || false);
  const [showRegresso, setShowRegresso] = useState(false);
  const [regressoData, setRegressoData] = useState({ data_regresso: '', motivo_volta: '' });
  const [photoPopup, setPhotoPopup] = useState(null);
  const [doacaoSeqData, setDoacaoSeqData] = useState(animal.doacao_sequestro_data || '');
  const [doacaoSeqObs, setDoacaoSeqObs] = useState(animal.doacao_sequestro_obs || '');
  const [doacaoSeq, setDoacaoSeq] = useState(animal.doacao_sequestro || false);
  const [entradaDoacao, setEntradaDoacao] = useState(animal.entrada_doacao || false);
  const [entradaDoacaoData, setEntradaDoacaoData] = useState(animal.entrada_doacao_data || '');
  const [entradaDoacaoObs, setEntradaDoacaoObs] = useState(animal.entrada_doacao_obs || '');
  const [perfis, setPerfis] = useState([]);
  const [perfisOpen, setPerfisOpen] = useState(false);
  const [compNotas, setCompNotas] = useState(animal.comportamento_notas || '');

  useEffect(() => {
    axios.get(`${API}/animais/perfis-comportamento`).then(r => setPerfis(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!animal) return;
    Promise.all([
      axios.get(`${API}/animais/${animal.id}/vacinas`), axios.get(`${API}/animais/${animal.id}/testes`),
      axios.get(`${API}/animais/${animal.id}/tratamentos`), axios.get(`${API}/animais/${animal.id}/desparasitacoes`)
    ]).then(([v, t, tr, d]) => { setVacinas(v.data); setTestes(t.data); setTratamentos(tr.data); setDesps(d.data); }).catch(() => {});
  }, [animal]);

  if (!animal) return null;

  const salvarObs = async () => { await axios.put(`${API}/animais/${animal.id}`, { observacoes: obs }); handleUpdate(); };
  const selectPerfil = async (val) => {
    if (val === '__add__') {
      const nome = window.prompt('Nome do novo perfil de comportamento:');
      if (!nome || !nome.trim()) return;
      try {
        const r = await axios.post(`${API}/animais/perfis-comportamento`, { nome: nome.trim() });
        setPerfis(prev => prev.some(p => p.id === r.data.id) ? prev : [...prev, r.data].sort((a, b) => a.nome.localeCompare(b.nome)));
        await axios.put(`${API}/animais/${animal.id}`, { comportamento: r.data.nome });
      } catch (e) { alert(e.response?.data?.detail || 'Erro ao criar perfil'); return; }
    } else {
      await axios.put(`${API}/animais/${animal.id}`, { comportamento: val || null });
    }
    handleUpdate();
  };
  const salvarCompNotas = async () => { await axios.put(`${API}/animais/${animal.id}`, { comportamento_notas: compNotas }); handleUpdate(); };
  const eliminarPerfil = async (p) => {
    if (!window.confirm(`Eliminar o perfil "${p.nome}"?\n\nDeixará de aparecer nas opções e no filtro de pesquisa. Os animais que o têm atribuído mantêm o registo.`)) return;
    try {
      await axios.delete(`${API}/animais/perfis-comportamento/${p.id}`);
      setPerfis(prev => prev.filter(x => x.id !== p.id));
    } catch (e) { alert(e.response?.data?.detail || 'Erro ao eliminar perfil'); }
  };
  const salvarObsAdmin = async () => { await axios.put(`${API}/animais/${animal.id}`, { obs_administrativa: obsAdmin }); handleUpdate(); };
  const toggleAdocao = async (v) => { setDispAdocao(v); await axios.put(`${API}/animais/${animal.id}`, { disponivel_adocao: v }); handleUpdate(); };
  const toggleDoacaoSeq = async (v) => { setDoacaoSeq(v); await axios.put(`${API}/animais/${animal.id}`, { doacao_sequestro: v }); handleUpdate(); };
  const salvarDoacaoSeq = async () => { await axios.put(`${API}/animais/${animal.id}`, { doacao_sequestro: doacaoSeq, doacao_sequestro_data: doacaoSeqData, doacao_sequestro_obs: doacaoSeqObs }); handleUpdate(); };
  const toggleEntradaDoacao = async (v) => { setEntradaDoacao(v); await axios.put(`${API}/animais/${animal.id}`, { entrada_doacao: v }); handleUpdate(); };
  const salvarEntradaDoacao = async () => { await axios.put(`${API}/animais/${animal.id}`, { entrada_doacao: entradaDoacao, entrada_doacao_data: entradaDoacaoData, entrada_doacao_obs: entradaDoacaoObs }); handleUpdate(); };

  const addVacina = async () => { await axios.post(`${API}/animais/${animal.id}/vacinas`, newVacina); setVacinas((await axios.get(`${API}/animais/${animal.id}/vacinas`)).data); setShowAddVacina(false); setNewVacina({ nome: VACCINE_OPTIONS[0], data: '', proxima_data: '', observacao: '' }); handleUpdate(); };
  const addTeste = async () => { await axios.post(`${API}/animais/${animal.id}/testes`, newTeste); setTestes((await axios.get(`${API}/animais/${animal.id}/testes`)).data); setShowAddTeste(false); setNewTeste({ tipo: TEST_TYPES[0], sub_resultados: {}, data: '', observacao: '' }); handleUpdate(); };
  const addTrat = async () => { await axios.post(`${API}/animais/${animal.id}/tratamentos`, newTrat); setTratamentos((await axios.get(`${API}/animais/${animal.id}/tratamentos`)).data); setShowAddTrat(false); setNewTrat({ nome: '', descricao: '', protocolo: '', data_inicio: '', data_fim: '' }); };
  const addDesp = async () => { await axios.post(`${API}/animais/${animal.id}/desparasitacoes`, newDesp); setDesps((await axios.get(`${API}/animais/${animal.id}/desparasitacoes`)).data); setShowAddDesp(false); setNewDesp({ produto: DEWORMING_PRODUCTS[0], data: '', produto_outro: '', observacao: '' }); handleUpdate(); };
  const handleExit = async () => { await axios.put(`${API}/animais/${animal.id}/saida`, { ...exitData, motivo_saida: showExit }); handleUpdate(); onClose(); };
  const handleRegresso = async () => { await axios.put(`${API}/animais/${animal.id}/regresso`, regressoData); setShowRegresso(false); handleUpdate(); onClose(); };
  const removeAnimal = () => { setDeleteText(''); setShowDeleteModal(true); };
  const confirmRemoveAnimal = async () => {
    if (deleteText.trim().toUpperCase() !== 'ELIMINAR') return;
    setDeleting(true);
    try { await axios.delete(`${API}/animais/${animal.id}`); setShowDeleteModal(false); handleUpdate(); onClose(); }
    finally { setDeleting(false); }
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    e.target.value = '';
    // Open cropper first — user adjusts each photo, then we upload
    setCropperFiles(files);
  };

  const startEditExistingPhoto = async (index) => {
    const url = animal.fotos?.[index];
    if (!url) return;
    try {
      const realUrl = resolveUrl(url);
      const r = await fetch(realUrl);
      const blob = await r.blob();
      const file = new File([blob], `foto_${index}.jpg`, { type: blob.type || 'image/jpeg' });
      setEditingPhotoIndex(index);
      setCropperFiles([file]);
    } catch (err) {
      alert('Não foi possível carregar a foto para edição. Tenta novamente.');
    }
  };

  const uploadAdjustedPhotos = async (adjustedFiles) => {
    const replacingIndex = editingPhotoIndex;
    setCropperFiles(null);
    setEditingPhotoIndex(null);
    if (!adjustedFiles || adjustedFiles.length === 0) return;
    setUploadingPhoto(true);
    let okCount = 0;
    const errors = [];
    const sleep = (ms) => new Promise(r => setTimeout(r, ms));
    try {
      for (let i = 0; i < adjustedFiles.length; i++) {
        const f = adjustedFiles[i];
        setUploadProgress(`${i + 1}/${adjustedFiles.length}`);
        const compressed = await compressImageFile(f);
        let lastErr = null;
        let success = false;
        for (let attempt = 1; attempt <= 3; attempt++) {
          try {
            const fd = new FormData();
            fd.append('file', compressed);
            await axios.post(`${API}/animais/${animal.id}/fotos`, fd, {
              headers: { 'Content-Type': 'multipart/form-data' },
              timeout: 180000,
            });
            success = true; okCount++; break;
          } catch (err) {
            lastErr = err;
            if (attempt < 3) await sleep(800 * attempt);
          }
        }
        if (!success) errors.push({ filename: f.name, error: lastErr?.response?.data?.detail || lastErr?.message || 'Erro' });
        if (i < adjustedFiles.length - 1) await sleep(250);
      }
      // If editing existing photo: delete the OLD one after the new one is successfully uploaded
      if (replacingIndex !== null && okCount > 0) {
        try {
          await axios.delete(`${API}/animais/${animal.id}/fotos/${replacingIndex}`);
        } catch (err) { /* non-fatal */ }
      }
      await refreshAnimal();
      onUpdate?.();
      if (errors.length > 0) {
        alert(`Carregadas ${okCount} de ${adjustedFiles.length} foto(s).\nFalharam:\n${errors.map(x => `• ${x.filename}: ${x.error}`).join('\n')}`);
      }
    } finally {
      setUploadingPhoto(false);
      setUploadProgress('');
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    e.target.value = '';
    setUploadingFile(true);
    const errors = [];
    let okCount = 0;
    const sleep = (ms) => new Promise(r => setTimeout(r, ms));
    try {
      for (let i = 0; i < files.length; i++) {
        const f = files[i];
        setUploadProgress(`${i + 1}/${files.length}`);
        let lastErr = null;
        let success = false;
        for (let attempt = 1; attempt <= 3; attempt++) {
          try {
            const fd = new FormData();
            fd.append('file', f);
            await axios.post(`${API}/animais/${animal.id}/ficheiros`, fd, {
              headers: { 'Content-Type': 'multipart/form-data' },
              timeout: 180000,
            });
            success = true;
            okCount++;
            break;
          } catch (err) {
            lastErr = err;
            if (attempt < 3) await sleep(800 * attempt);
          }
        }
        if (!success) errors.push({ filename: f.name, error: lastErr?.response?.data?.detail || lastErr?.message || 'Erro' });
        if (i < files.length - 1) await sleep(250);
      }
      await refreshAnimal();
      onUpdate?.();
      if (errors.length > 0) {
        alert(`Carregados ${okCount} de ${files.length} ficheiro(s).\nFalharam:\n${errors.map(x => `• ${x.filename}: ${x.error}`).join('\n')}`);
      }
    } finally {
      setUploadingFile(false);
      setUploadProgress('');
    }
  };

  const getSubResultFields = (tipo) => {
    switch (tipo) {
      case 'FIV e FELV': return ['FIV', 'FELV'];
      case 'Teste de Quatro': return ['Dirofilariose', 'Leishmaniose', 'Ehrlichiose', 'Anaplasmose'];
      case 'Parvo Corona': return ['Parvovírus', 'Coronavírus'];
      case 'Leishmaniose': return ['Leishmaniose'];
      default: return [];
    }
  };

  const fotoUrl = animal.fotos?.length > 0 ? resolveUrl(animal.fotos[animal.foto_capa_index || 0]) : placeholderImg;
  const lastVacina = vacinas[0];
  const lastTeste = testes[0];
  const lastDesp = desps[0];
  const activeTrat = tratamentos.find(t => t.ativo);
  const isTitularEntry = animal.motivo_entrada === 'Entrega por titular/detentor';
  const isSequestroEntry = animal.motivo_entrada === 'Sequestro' || animal.motivo_entrada === 'Recolha ao domicílio' || animal.is_sequestro;

  const isOp = authUser?.role === 'operacional';
  const allTabs = [
    { id: 'info', label: 'Informações' }, { id: 'admin', label: 'Info Administrativa' },
    { id: 'vacinas', label: 'Vacinas' }, { id: 'testes', label: 'Ficha Médica' },
    { id: 'tratamentos', label: 'Tratamentos' }, { id: 'desp', label: 'Desparasitação' },
    { id: 'ficheiros', label: 'Ficheiros' },
  ];
  // Operacionais só veem informações principais + ficheiros (para carregar fotos)
  const tabs = isOp ? allTabs.filter(t => t.id === 'info' || t.id === 'ficheiros') : allTabs;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" data-testid="animal-detail-modal">
      <div className="bg-card rounded-2xl border border-border shadow-2xl w-full max-w-4xl max-h-[92vh] overflow-hidden flex flex-col m-4 relative">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="font-heading font-bold text-xl text-foreground">Ficha de {animal.nome}</h2>
          <button onClick={onClose} data-testid="close-detail" className="p-1 hover:bg-muted rounded-full"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex gap-1 px-4 pt-3 border-b border-border overflow-x-auto">
          {tabs.map(t => (
            <button key={t.id} data-testid={`tab-${t.id}`} onClick={() => setTab(t.id)}
              className={`px-3 py-2 text-xs font-medium rounded-t-lg whitespace-nowrap transition-all ${tab === t.id ? 'bg-primary/10 text-primary border-b-2 border-primary' : 'text-muted-foreground hover:text-foreground'}`}>
              {t.label}
            </button>
          ))}
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-4">

          {tab === 'info' && (<>
            <div className="flex gap-4 flex-col sm:flex-row">
              <div className="relative w-full sm:w-48 h-48 rounded-xl overflow-hidden flex-shrink-0 cursor-pointer" onClick={() => setPhotoPopup(fotoUrl)}>
                <img src={fotoUrl} alt={animal.nome} className="w-full h-full object-cover" onError={e => { e.target.src = placeholderImg; }} />
                {animal.cor_comportamento && <span className="absolute top-2 right-2 w-5 h-5 rounded-full border-2 border-white shadow" style={{ backgroundColor: BEHAVIOR_COLORS[animal.cor_comportamento] }} />}
                {animal.titular && <span className={`absolute top-2 left-2 ${animal.data_transferencia_cro ? 'bg-green-600' : 'bg-blue-600'} text-white text-[10px] px-1.5 py-0.5 rounded-full flex items-center gap-0.5`} data-testid="titular-badge"><User className="w-2.5 h-2.5" />Titular</span>}
                {!animal.titular && <span className="absolute top-2 left-2 bg-gray-500 text-white text-[10px] px-1.5 py-0.5 rounded-full">Sem Titular</span>}
                <label className="absolute bottom-2 right-2 bg-black/50 text-white rounded-full p-1.5 cursor-pointer hover:bg-black/70" title="Adicionar fotos">
                  {uploadingPhoto ? <span className="text-[10px] px-1">{uploadProgress || '…'}</span> : <Upload className="w-3.5 h-3.5" />}
                  <input type="file" accept="image/*" multiple className="hidden" onChange={handlePhotoUpload} disabled={uploadingPhoto} />
                </label>
              </div>
              <div className="flex-1 space-y-1.5 text-sm">
                <div className="flex flex-wrap gap-3 mb-2">
                  {animal.chip && (
                    <div className="bg-primary/10 border border-primary/30 rounded-xl px-3 py-1.5">
                      <div className="text-[10px] uppercase tracking-wide text-primary/80 font-semibold">Chip</div>
                      <div className="font-bold text-lg tracking-wider text-primary" data-testid="detail-chip">{animal.chip}</div>
                    </div>
                  )}
                  {animal.num_processo && (
                    <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl px-3 py-1.5">
                      <div className="text-[10px] uppercase tracking-wide text-amber-700 font-semibold">Nº Processo</div>
                      <div className="font-bold text-lg tracking-wider text-amber-700">{animal.num_processo}</div>
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-x-6 gap-y-1.5">
                  <InfoRow label="Espécie" value={animal.especie} />
                  <InfoRow label="Sexo" value={animal.sexo} />
                  <InfoRow label="Cor" value={animal.cor} />
                  <InfoRow label="Raça" value={animal.raca} />
                  <InfoRow label="Idade" value={idadeDoAnimal(animal)} />
                  <InfoRow label="Data de Nascimento" value={fmtDate(animal.data_nascimento)} />
                  <InfoRow label="Peso" value={animal.peso ? `${animal.peso} kg` : null} />
                  <InfoRow label="Box" value={animal.box} />
                  <InfoRow label="Entrada" value={fmtDate(animal.entrada)} />
                  <InfoRow label="Esterilizado" value={animal.esterilizado ? 'Sim' : 'Não'} />
                </div>
                <hr className="border-border my-2" />
                <div className="space-y-1">
                  <InfoRow label="Última Vacina" value={lastVacina ? `${lastVacina.nome} — ${fmtDate(lastVacina.data)} (há ${daysSince(lastVacina.data) ?? '?'} dias)` : 'N/D'} />
                  <InfoRow label="Último Teste" value={lastTeste ? `${lastTeste.tipo} — ${fmtDate(lastTeste.data)} (há ${daysSince(lastTeste.data) ?? '?'} dias)` : 'N/D'} />
                  <InfoRow label="Última Desparasitação" value={lastDesp ? `${lastDesp.produto} — ${fmtDate(lastDesp.data)} (há ${daysSince(lastDesp.data) ?? '?'} dias)` : 'N/D'} />
                  <InfoRow label="Tratamento Ativo" value={activeTrat ? `${activeTrat.nome} — início ${fmtDate(activeTrat.data_inicio)} (há ${daysSince(activeTrat.data_inicio) ?? '?'} dias)` : 'Nenhum'} />
                </div>
                {animal.titular && <InfoRow label="Dados Titular" value={animal.dados_titular} />}
                {animal.titular_original && animal.data_transferencia_cro && <InfoRow label="Titular Original" value={`${animal.titular_original} (transf. ${fmtDate(animal.data_transferencia_cro)})`} />}
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium block mb-1">Perfil de Comportamento</label>
                <div className="relative">
                  <button type="button" data-testid="comportamento-select" onClick={() => setPerfisOpen(o => !o)}
                    className="w-full flex items-center justify-between rounded-xl border border-border bg-background px-3 py-2 text-sm">
                    <span>{animal.comportamento || 'Sem perfil'}</span>
                    <ChevronDown className="w-4 h-4 text-muted-foreground" />
                  </button>
                  {perfisOpen && (<>
                    <div className="fixed inset-0 z-20" onClick={() => setPerfisOpen(false)} />
                    <div className="absolute z-30 mt-1 w-full bg-card border border-border rounded-xl shadow-lg max-h-56 overflow-auto" data-testid="perfis-dropdown">
                      <button type="button" onClick={() => { setPerfisOpen(false); selectPerfil(''); }} data-testid="perfil-opt-none"
                        className="w-full text-left px-3 py-2 text-sm text-muted-foreground hover:bg-muted">Sem perfil</button>
                      {perfis.map(p => (
                        <div key={p.id} className={`flex items-center hover:bg-muted ${animal.comportamento === p.nome ? 'bg-primary/10' : ''}`}>
                          <button type="button" onClick={() => { setPerfisOpen(false); selectPerfil(p.nome); }} data-testid={`perfil-opt-${p.nome}`}
                            className="flex-1 text-left px-3 py-2 text-sm">{p.nome}</button>
                          <button type="button" onClick={(e) => { e.stopPropagation(); eliminarPerfil(p); }} title={`Eliminar perfil ${p.nome}`}
                            data-testid={`perfil-del-${p.nome}`} className="px-2 py-2 text-muted-foreground hover:text-destructive">
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ))}
                      <button type="button" onClick={() => { setPerfisOpen(false); selectPerfil('__add__'); }} data-testid="perfil-add-btn"
                        className="w-full text-left px-3 py-2 text-sm text-primary hover:bg-muted border-t border-border">➕ Adicionar perfil…</button>
                    </div>
                  </>)}
                </div>
              </div>
              <div>
                <label className="text-sm font-medium block mb-1">Notas de comportamento</label>
                <textarea data-testid="comportamento-notas" rows={2} value={compNotas} onChange={e => setCompNotas(e.target.value)}
                  className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm" />
                <button onClick={salvarCompNotas} data-testid="save-comportamento-notas" className="mt-1 text-xs bg-muted text-foreground px-3 py-1.5 rounded-full hover:bg-border">Guardar</button>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">Observações</label>
              <textarea data-testid="obs-textarea" rows={2} value={obs} onChange={e => setObs(e.target.value)} className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm" />
              <button onClick={salvarObs} data-testid="save-obs" className="mt-1 text-xs bg-muted text-foreground px-3 py-1.5 rounded-full hover:bg-border">Guardar</button>
            </div>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={dispAdocao} onChange={e => toggleAdocao(e.target.checked)} className="rounded" />Disponível para Adoção</label>
          </>)}

          {tab === 'admin' && (<>
            <h3 className="font-heading font-bold text-lg">Informação Administrativa</h3>
            <div className="space-y-1.5 text-sm">
              <InfoRow label="Motivo de Entrada" value={animal.motivo_entrada} />
              <InfoRow label="Data de Entrada" value={fmtDate(animal.entrada)} />
              {animal.num_processo && <InfoRow label="Nº de Processo" value={animal.num_processo} />}
              <InfoRow label="Titular" value={animal.titular ? 'Sim' : 'Não'} />
              {animal.titular && <InfoRow label="Dados do Titular" value={animal.dados_titular} />}
              {animal.data_transferencia_cro && <InfoRow label="Data Transferência CRO" value={fmtDate(animal.data_transferencia_cro)} />}
              {animal.titular_original && <InfoRow label="Titular Original" value={animal.titular_original} />}
              <InfoRow label="Concelho" value={animal.concelho} />
              {animal.freguesia && <InfoRow label="Freguesia" value={animal.freguesia} />}
              <InfoRow label="Local de Ocorrência" value={animal.local_ocorrencia} />
              {animal.num_porta && <InfoRow label="Nº de Porta" value={animal.num_porta} />}
              {animal.codigo_postal && <InfoRow label="Código Postal" value={animal.codigo_postal} />}
            </div>
            {animal.local_ocorrencia && (
              <div className="mt-3">
                <div className="flex items-center gap-2 mb-2">
                  <MapPin className="w-4 h-4 text-primary" />
                  <span className="text-sm font-medium">Localização no Mapa</span>
                </div>
                <LocationMap
                  lat={animal.local_ocorrencia_lat}
                  lng={animal.local_ocorrencia_lng}
                  label={animal.local_ocorrencia_display || animal.local_ocorrencia}
                />
                {animal.local_ocorrencia_display && (
                  <p className="text-xs text-muted-foreground mt-1 italic">{animal.local_ocorrencia_display}</p>
                )}
              </div>
            )}
            {isSequestroEntry && (
              <div className="mt-3 bg-accent/10 rounded-xl p-3 space-y-2">
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={doacaoSeq} onChange={e => toggleDoacaoSeq(e.target.checked)} className="rounded" /><Shield className="w-4 h-4 text-accent" />Animal de sequestro — Houve doação?</label>
                {doacaoSeq && (<>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <div><label className="text-xs text-muted-foreground">Data da Doação</label><DateInput value={doacaoSeqData} onChange={v => setDoacaoSeqData(v)} className="w-full" /></div>
                  </div>
                  <div><label className="text-xs text-muted-foreground">Observações da Doação</label><textarea rows={2} value={doacaoSeqObs} onChange={e => setDoacaoSeqObs(e.target.value)} className="w-full rounded-lg border border-border bg-background px-2 py-1.5 text-sm" /></div>
                  <button onClick={salvarDoacaoSeq} className="text-xs bg-muted text-foreground px-3 py-1.5 rounded-full hover:bg-border">Guardar</button>
                </>)}
              </div>
            )}
            {isTitularEntry && (
              <div className="mt-3 bg-green-50 rounded-xl p-3 space-y-2">
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={entradaDoacao} onChange={e => toggleEntradaDoacao(e.target.checked)} className="rounded" />Entrada por doação (titular/detentor) — não contabiliza como novo animal</label>
                {entradaDoacao && (<>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <div><label className="text-xs text-muted-foreground">Data da Doação</label><DateInput value={entradaDoacaoData} onChange={v => setEntradaDoacaoData(v)} className="w-full" /></div>
                  </div>
                  <div><label className="text-xs text-muted-foreground">Observações da Doação</label><textarea rows={2} value={entradaDoacaoObs} onChange={e => setEntradaDoacaoObs(e.target.value)} className="w-full rounded-lg border border-border bg-background px-2 py-1.5 text-sm" /></div>
                  <button onClick={salvarEntradaDoacao} className="text-xs bg-muted text-foreground px-3 py-1.5 rounded-full hover:bg-border">Guardar</button>
                </>)}
              </div>
            )}
            <div className="mt-3">
              <label className="text-sm font-medium block mb-1">Observações Administrativas</label>
              <textarea rows={3} value={obsAdmin} onChange={e => setObsAdmin(e.target.value)} className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm" />
              <button onClick={salvarObsAdmin} className="mt-1 text-xs bg-muted text-foreground px-3 py-1.5 rounded-full hover:bg-border">Guardar</button>
            </div>
          </>)}

          {tab === 'vacinas' && (<>
            <div className="flex justify-between items-center"><h3 className="font-heading font-bold text-lg">Histórico de Vacinas</h3>
              <button data-testid="add-vacina-btn" onClick={() => setShowAddVacina(!showAddVacina)} className="flex items-center gap-1 text-sm bg-primary text-primary-foreground rounded-full px-3 py-1.5"><Plus className="w-3.5 h-3.5" />Vacina</button></div>
            {showAddVacina && (
              <div className="bg-muted/50 rounded-xl p-3 space-y-2">
                <select value={newVacina.nome} onChange={e => setNewVacina({ ...newVacina, nome: e.target.value })} className="w-full rounded-lg border border-border bg-background px-2 py-1.5 text-sm">{VACCINE_OPTIONS.map(v => <option key={v}>{v}</option>)}</select>
                <div className="grid grid-cols-2 gap-2">
                  <div><label className="text-xs">Data</label><DateInput value={newVacina.data} onChange={v => setNewVacina({ ...newVacina, data: v })} className="w-full" /></div>
                  <div><label className="text-xs">Próxima Data</label><DateInput value={newVacina.proxima_data} onChange={v => setNewVacina({ ...newVacina, proxima_data: v })} className="w-full" /></div>
                </div>
                <input placeholder="Observação / Marca" value={newVacina.observacao} onChange={e => setNewVacina({ ...newVacina, observacao: e.target.value })} className="w-full rounded-lg border border-border bg-background px-2 py-1.5 text-sm" />
                <button data-testid="save-vacina" onClick={addVacina} className="text-xs bg-primary text-primary-foreground rounded-full px-3 py-1.5">Guardar</button>
              </div>
            )}
            <div className="space-y-2">{vacinas.map(v => (
              editVacinaId === v.id ? (
                <div key={v.id} className="bg-muted/60 rounded-xl p-3 space-y-2">
                  <select value={editVacina.nome} onChange={e => setEditVacina({ ...editVacina, nome: e.target.value })} className="w-full rounded-lg border border-border bg-background px-2 py-1.5 text-sm">{VACCINE_OPTIONS.map(o => <option key={o}>{o}</option>)}</select>
                  <div className="grid grid-cols-2 gap-2">
                    <div><label className="text-xs">Data</label><DateInput value={editVacina.data} onChange={x => setEditVacina({ ...editVacina, data: x })} className="w-full" /></div>
                    <div><label className="text-xs">Próxima Data</label><DateInput value={editVacina.proxima_data || ''} onChange={x => setEditVacina({ ...editVacina, proxima_data: x })} className="w-full" /></div>
                  </div>
                  <input placeholder="Observação / Marca" value={editVacina.observacao || ''} onChange={e => setEditVacina({ ...editVacina, observacao: e.target.value })} className="w-full rounded-lg border border-border bg-background px-2 py-1.5 text-sm" />
                  <div className="flex gap-2">
                    <button data-testid={`save-edit-vacina-${v.id}`} onClick={async () => {
                      try {
                        await axios.put(`${API}/vacinas/${v.id}`, editVacina);
                        const r = await axios.get(`${API}/animais/${animal.id}/vacinas`); setVacinas(r.data);
                        setEditVacinaId(null); handleUpdate();
                      } catch (err) { alert(err.response?.data?.detail || 'Erro a guardar'); }
                    }} className="text-xs bg-primary text-primary-foreground rounded-full px-3 py-1.5">Guardar</button>
                    <button onClick={() => setEditVacinaId(null)} className="text-xs bg-muted text-foreground rounded-full px-3 py-1.5">Cancelar</button>
                  </div>
                </div>
              ) : (
                <div key={v.id} className="flex justify-between items-start bg-muted/30 rounded-lg p-2.5 text-sm gap-2">
                  <div className="flex-1"><strong>{v.nome}</strong> — {fmtDate(v.data)} {v.proxima_data && <span className="text-muted-foreground">(próxima: {fmtDate(v.proxima_data)})</span>}{v.observacao && <span className="text-muted-foreground block text-xs mt-0.5">Obs: {v.observacao}</span>}</div>
                  <div className="flex gap-1 flex-shrink-0">
                    <button data-testid={`edit-vacina-${v.id}`} onClick={() => { setEditVacinaId(v.id); setEditVacina({ nome: v.nome, data: v.data || '', proxima_data: v.proxima_data || '', observacao: v.observacao || '' }); }} className="text-primary hover:bg-primary/10 p-1 rounded"><Edit className="w-3.5 h-3.5" /></button>
                    <button onClick={async () => { if (!window.confirm('Apagar vacina?')) return; await axios.delete(`${API}/vacinas/${v.id}`); setVacinas(vacinas.filter(x => x.id !== v.id)); handleUpdate(); }} className="text-destructive hover:bg-destructive/10 p-1 rounded"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
              )
            ))}{vacinas.length === 0 && <p className="text-sm text-muted-foreground">Sem vacinas registadas.</p>}</div>
          </>)}

          {tab === 'testes' && (<>
            <div className="flex justify-between items-center"><h3 className="font-heading font-bold text-lg">Histórico</h3>
              <button data-testid="add-teste-btn" onClick={() => setShowAddTeste(!showAddTeste)} className="flex items-center gap-1 text-sm bg-primary text-primary-foreground rounded-full px-3 py-1.5"><Plus className="w-3.5 h-3.5" />Adicionar</button></div>
            {showAddTeste && (
              <div className="bg-muted/50 rounded-xl p-3 space-y-2">
                <select data-testid="teste-tipo" value={newTeste.tipo} onChange={e => setNewTeste({ ...newTeste, tipo: e.target.value, sub_resultados: {}, observacao: '' })} className="w-full rounded-lg border border-border bg-background px-2 py-1.5 text-sm">{TEST_TYPES.map(t => <option key={t}>{t}</option>)}</select>
                {OBS_ONLY_TESTS.has(newTeste.tipo) ? (
                  <textarea data-testid="teste-obs" rows={2} placeholder="Observações"
                    value={newTeste.observacao || ''} onChange={e => setNewTeste({ ...newTeste, observacao: e.target.value })}
                    className="w-full rounded-lg border border-border bg-background px-2 py-1.5 text-sm" />
                ) : (
                  <div className="space-y-1">{getSubResultFields(newTeste.tipo).map(f => (
                    <div key={f} className="flex items-center gap-2"><span className="text-xs w-28">{f}:</span>
                      <select value={newTeste.sub_resultados[f.toLowerCase()] || ''} onChange={e => setNewTeste({ ...newTeste, sub_resultados: { ...newTeste.sub_resultados, [f.toLowerCase()]: e.target.value } })} className="flex-1 rounded-lg border border-border bg-background px-2 py-1 text-xs"><option value="">—</option><option value="pos">Positivo</option><option value="neg">Negativo</option></select></div>
                  ))}</div>
                )}
                <DateInput value={newTeste.data} onChange={v => setNewTeste({ ...newTeste, data: v })} className="w-full" />
                <button data-testid="save-teste" onClick={addTeste} className="text-xs bg-primary text-primary-foreground rounded-full px-3 py-1.5">Guardar</button>
              </div>
            )}
            <div className="space-y-2">{testes.map(t => (
              <div key={t.id} className="bg-muted/30 rounded-lg p-2.5 text-sm">
                <div className="flex justify-between"><strong>{t.tipo}</strong><span className="text-xs text-muted-foreground">{fmtDate(t.data)}</span></div>
                {t.observacao && <p className="text-xs text-muted-foreground mt-1 whitespace-pre-wrap">{t.observacao}</p>}
                <div className="flex flex-wrap gap-1 mt-1">{Object.entries(t.sub_resultados || {}).map(([k, v]) => (
                  <span key={k} className={`text-xs px-2 py-0.5 rounded-full ${v === 'pos' ? 'bg-destructive/10 text-destructive' : 'bg-green-100 text-green-800'}`}>{k}: {v === 'pos' ? 'Pos' : 'Neg'}</span>
                ))}</div></div>
            ))}{testes.length === 0 && <p className="text-sm text-muted-foreground">Sem registos.</p>}</div>
          </>)}

          {tab === 'tratamentos' && (<>
            <div className="flex justify-between items-center"><h3 className="font-heading font-bold text-lg">Tratamentos</h3>
              <button data-testid="add-trat-btn" onClick={() => setShowAddTrat(!showAddTrat)} className="flex items-center gap-1 text-sm bg-primary text-primary-foreground rounded-full px-3 py-1.5"><Plus className="w-3.5 h-3.5" />Tratamento</button></div>
            {showAddTrat && (
              <div className="bg-muted/50 rounded-xl p-3 space-y-2">
                <input placeholder="Nome do tratamento" value={newTrat.nome} onChange={e => setNewTrat({ ...newTrat, nome: e.target.value })} className="w-full rounded-lg border border-border bg-background px-2 py-1.5 text-sm" />
                <textarea placeholder="Descrição" rows={2} value={newTrat.descricao} onChange={e => setNewTrat({ ...newTrat, descricao: e.target.value })} className="w-full rounded-lg border border-border bg-background px-2 py-1.5 text-sm" />
                <input placeholder="Protocolo Terapêutico" value={newTrat.protocolo} onChange={e => setNewTrat({ ...newTrat, protocolo: e.target.value })} className="w-full rounded-lg border border-border bg-background px-2 py-1.5 text-sm" />
                <div className="grid grid-cols-2 gap-2">
                  <div><label className="text-xs">Data Início</label><DateInput value={newTrat.data_inicio} onChange={v => setNewTrat({ ...newTrat, data_inicio: v })} className="w-full" /></div>
                  <div><label className="text-xs">Data Fim</label><DateInput value={newTrat.data_fim} onChange={v => setNewTrat({ ...newTrat, data_fim: v })} className="w-full" /></div>
                </div>
                <button data-testid="save-trat" onClick={addTrat} className="text-xs bg-primary text-primary-foreground rounded-full px-3 py-1.5">Guardar</button>
              </div>
            )}
            {(() => {
              const ativos = tratamentos.filter(t => t.ativo);
              const historico = tratamentos.filter(t => !t.ativo);
              const renderCard = (t) => (
                <div key={t.id} className="bg-muted/30 rounded-lg p-2.5 text-sm">
                  {editTratId === t.id ? (
                    <div className="space-y-2">
                      <input value={editTrat.nome} onChange={e => setEditTrat({ ...editTrat, nome: e.target.value })} placeholder="Nome" className="w-full rounded-lg border border-border bg-background px-2 py-1.5 text-sm" />
                      <textarea rows={2} value={editTrat.descricao || ''} onChange={e => setEditTrat({ ...editTrat, descricao: e.target.value })} placeholder="Descrição" className="w-full rounded-lg border border-border bg-background px-2 py-1.5 text-sm" />
                      <input value={editTrat.protocolo || ''} onChange={e => setEditTrat({ ...editTrat, protocolo: e.target.value })} placeholder="Protocolo" className="w-full rounded-lg border border-border bg-background px-2 py-1.5 text-sm" />
                      <div className="grid grid-cols-2 gap-2">
                        <div><label className="text-xs">Início</label><DateInput value={editTrat.data_inicio} onChange={v => setEditTrat({ ...editTrat, data_inicio: v })} className="w-full" /></div>
                        <div><label className="text-xs">Fim</label><DateInput value={editTrat.data_fim} onChange={v => setEditTrat({ ...editTrat, data_fim: v })} className="w-full" /></div>
                      </div>
                      <div className="flex gap-2 justify-end">
                        <button onClick={() => setEditTratId(null)} className="text-xs bg-muted px-3 py-1 rounded-full">Cancelar</button>
                        <button data-testid={`save-edit-trat-${t.id}`} onClick={async () => {
                          await axios.put(`${API}/tratamentos/${t.id}`, editTrat);
                          setTratamentos((await axios.get(`${API}/animais/${animal.id}/tratamentos`)).data);
                          setEditTratId(null);
                        }} className="text-xs bg-primary text-primary-foreground px-3 py-1 rounded-full">Guardar</button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex justify-between items-start gap-2">
                      <div className="flex-1">
                        <strong>{t.nome}</strong> <span className={`text-xs px-1.5 py-0.5 rounded-full ml-1 ${t.ativo ? 'bg-green-100 text-green-800' : 'bg-muted text-muted-foreground'}`}>{t.ativo ? 'Ativo' : 'Terminado'}</span>
                        <p className="text-xs text-muted-foreground mt-0.5">Início: {fmtDate(t.data_inicio)} {t.data_fim && `| Fim: ${fmtDate(t.data_fim)}`}</p>
                        {t.protocolo && <p className="text-xs text-muted-foreground">Protocolo: {t.protocolo}</p>}
                        {t.descricao && <p className="text-xs text-muted-foreground">{t.descricao}</p>}
                        {t.prescricoes_dia && Object.keys(t.prescricoes_dia).some(k => (t.prescricoes_dia[k] || '').trim()) && (
                          <div className="text-xs text-muted-foreground mt-1">
                            <span className="font-medium">Prescrições:</span>{' '}
                            {['sabado', 'domingo', 'segunda', 'terca', 'quarta', 'quinta', 'sexta']
                              .filter(k => (t.prescricoes_dia[k] || '').trim())
                              .map(k => `${k.slice(0, 3)}: ${t.prescricoes_dia[k]}`).join(' · ')}
                          </div>
                        )}
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        <button data-testid={`edit-trat-${t.id}`} title="Editar" onClick={() => { setEditTratId(t.id); setEditTrat({ nome: t.nome, descricao: t.descricao || '', protocolo: t.protocolo || '', data_inicio: t.data_inicio || '', data_fim: t.data_fim || '' }); }}
                          className="text-primary hover:bg-primary/10 p-1 rounded"><Edit className="w-3.5 h-3.5" /></button>
                        <button onClick={async () => { await axios.put(`${API}/tratamentos/${t.id}`, { ativo: !t.ativo }); setTratamentos((await axios.get(`${API}/animais/${animal.id}/tratamentos`)).data); }}
                          className="text-xs bg-muted px-2 py-1 rounded-full">{t.ativo ? 'Terminar' : 'Reativar'}</button>
                        <button data-testid={`del-trat-${t.id}`} title="Apagar" onClick={async () => {
                          if (!window.confirm('Apagar este tratamento?')) return;
                          await axios.delete(`${API}/tratamentos/${t.id}`);
                          setTratamentos(tratamentos.filter(x => x.id !== t.id));
                        }} className="text-destructive hover:bg-destructive/10 p-1 rounded"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    </div>
                  )}
                </div>
              );
              return (
                <div className="space-y-4">
                  <div>
                    <p className="text-xs font-semibold uppercase text-muted-foreground mb-1">Ativos ({ativos.length})</p>
                    <div className="space-y-2">
                      {ativos.length === 0 ? <p className="text-sm text-muted-foreground">Sem tratamentos ativos.</p> : ativos.map(renderCard)}
                    </div>
                  </div>
                  {historico.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold uppercase text-muted-foreground mb-1" data-testid="historico-tratamentos-label">Histórico — Terminados ({historico.length})</p>
                      <div className="space-y-2 opacity-90">{historico.map(renderCard)}</div>
                    </div>
                  )}
                </div>
              );
            })()}
          </>)}

          {tab === 'desp' && (<>
            <div className="flex justify-between items-center"><h3 className="font-heading font-bold text-lg">Desparasitação</h3>
              <button data-testid="add-desp-btn" onClick={() => setShowAddDesp(!showAddDesp)} className="flex items-center gap-1 text-sm bg-primary text-primary-foreground rounded-full px-3 py-1.5"><Plus className="w-3.5 h-3.5" />Desparasitação</button></div>
            {showAddDesp && (
              <div className="bg-muted/50 rounded-xl p-3 space-y-2">
                <select value={newDesp.produto} onChange={e => setNewDesp({ ...newDesp, produto: e.target.value })} className="w-full rounded-lg border border-border bg-background px-2 py-1.5 text-sm">{DEWORMING_PRODUCTS.map(d => <option key={d}>{d}</option>)}</select>
                {newDesp.produto === 'Outra' && <input placeholder="Qual produto?" value={newDesp.produto_outro} onChange={e => setNewDesp({ ...newDesp, produto_outro: e.target.value })} className="w-full rounded-lg border border-border bg-background px-2 py-1.5 text-sm" />}
                <DateInput value={newDesp.data} onChange={v => setNewDesp({ ...newDesp, data: v })} className="w-full" />
                <input placeholder="Observação (opcional)" value={newDesp.observacao || ''} onChange={e => setNewDesp({ ...newDesp, observacao: e.target.value })} className="w-full rounded-lg border border-border bg-background px-2 py-1.5 text-sm" />
                <button data-testid="save-desp" onClick={addDesp} className="text-xs bg-primary text-primary-foreground rounded-full px-3 py-1.5">Guardar</button>
              </div>
            )}
            <div className="space-y-2">{desps.map(d => (
              editDespId === d.id ? (
                <div key={d.id} className="bg-muted/60 rounded-xl p-3 space-y-2">
                  <select value={editDesp.produto} onChange={e => setEditDesp({ ...editDesp, produto: e.target.value })} className="w-full rounded-lg border border-border bg-background px-2 py-1.5 text-sm">{DEWORMING_PRODUCTS.map(o => <option key={o}>{o}</option>)}</select>
                  {editDesp.produto === 'Outra' && <input placeholder="Qual produto?" value={editDesp.produto_outro || ''} onChange={e => setEditDesp({ ...editDesp, produto_outro: e.target.value })} className="w-full rounded-lg border border-border bg-background px-2 py-1.5 text-sm" />}
                  <DateInput value={editDesp.data} onChange={x => setEditDesp({ ...editDesp, data: x })} className="w-full" />
                  <input placeholder="Observação (opcional)" value={editDesp.observacao || ''} onChange={e => setEditDesp({ ...editDesp, observacao: e.target.value })} className="w-full rounded-lg border border-border bg-background px-2 py-1.5 text-sm" />
                  <div className="flex gap-2">
                    <button data-testid={`save-edit-desp-${d.id}`} onClick={async () => {
                      try {
                        await axios.put(`${API}/desparasitacoes/${d.id}`, editDesp);
                        const r = await axios.get(`${API}/animais/${animal.id}/desparasitacoes`); setDesps(r.data);
                        setEditDespId(null); handleUpdate();
                      } catch (err) { alert(err.response?.data?.detail || 'Erro a guardar'); }
                    }} className="text-xs bg-primary text-primary-foreground rounded-full px-3 py-1.5">Guardar</button>
                    <button onClick={() => setEditDespId(null)} className="text-xs bg-muted text-foreground rounded-full px-3 py-1.5">Cancelar</button>
                  </div>
                </div>
              ) : (
                <div key={d.id} className="flex justify-between items-start bg-muted/30 rounded-lg p-2.5 text-sm gap-2">
                  <div className="flex-1"><strong>{d.produto}</strong>{d.produto_outro ? ` (${d.produto_outro})` : ''} — {fmtDate(d.data)}{d.observacao && <span className="text-muted-foreground block text-xs mt-0.5">Obs: {d.observacao}</span>}</div>
                  <div className="flex gap-1 flex-shrink-0">
                    <button data-testid={`edit-desp-${d.id}`} onClick={() => { setEditDespId(d.id); setEditDesp({ produto: d.produto || DEWORMING_PRODUCTS[0], data: d.data || '', produto_outro: d.produto_outro || '', observacao: d.observacao || '' }); }} className="text-primary hover:bg-primary/10 p-1 rounded"><Edit className="w-3.5 h-3.5" /></button>
                    <button data-testid={`delete-desp-${d.id}`} onClick={async () => { if (!window.confirm('Apagar desparasitação?')) return; try { await axios.delete(`${API}/desparasitacoes/${d.id}`); setDesps(desps.filter(x => x.id !== d.id)); handleUpdate(); } catch (err) { alert(err.response?.data?.detail || 'Erro a apagar'); } }} className="text-destructive hover:bg-destructive/10 p-1 rounded"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
              )
            ))}{desps.length === 0 && <p className="text-sm text-muted-foreground">Sem desparasitações.</p>}</div>
          </>)}

          {tab === 'ficheiros' && (<>
            <div className="flex justify-between items-center"><h3 className="font-heading font-bold text-lg">Ficheiros</h3>
              <label className="flex items-center gap-1 text-sm bg-primary text-primary-foreground rounded-full px-3 py-1.5 cursor-pointer">
                <Upload className="w-3.5 h-3.5" />{uploadingFile ? `A enviar… ${uploadProgress}` : 'Upload'}
                <input type="file" multiple className="hidden" onChange={handleFileUpload} disabled={uploadingFile} />
              </label></div>
            <div className="space-y-2">{(animal.ficheiros || []).map((f, i) => (
              <div key={i} className="flex items-center gap-2 bg-muted/30 rounded-lg p-2 text-sm group">
                <a href={resolveUrl(f.url)} target="_blank" rel="noreferrer" className="flex items-center gap-2 flex-1 min-w-0 hover:bg-muted">
                  <FileText className="w-4 h-4 flex-shrink-0" /><span className="truncate">{f.nome}</span>
                </a>
                <button data-testid={`delete-ficheiro-${i}`} title="Apagar ficheiro"
                  onClick={async () => {
                    if (!window.confirm(`Apagar o ficheiro "${f.nome}"?`)) return;
                    try {
                      await axios.delete(`${API}/animais/${animal.id}/ficheiros/${i}`);
                      handleUpdate();
                    } catch (err) { alert(err.response?.data?.detail || 'Erro a apagar ficheiro'); }
                  }}
                  className="text-destructive hover:bg-destructive/10 p-1 rounded">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}{(!animal.ficheiros || animal.ficheiros.length === 0) && <p className="text-sm text-muted-foreground">Sem ficheiros.</p>}</div>
            <div className="mt-4">
              <div className="flex justify-between items-center mb-2">
                <h4 className="font-heading font-bold text-sm">Fotos ({animal.fotos?.length || 0})</h4>
                <label className="flex items-center gap-1 text-xs bg-primary text-primary-foreground rounded-full px-3 py-1 cursor-pointer">
                  <Upload className="w-3 h-3" />{uploadingPhoto ? `A enviar… ${uploadProgress}` : 'Adicionar fotos'}
                  <input type="file" accept="image/*" multiple className="hidden" onChange={handlePhotoUpload} disabled={uploadingPhoto} />
                </label>
              </div>
              <div className="grid grid-cols-4 gap-2">{(animal.fotos || []).map((f, i) => (
                <div key={`${i}-${typeof f === 'string' ? f.slice(-40) : i}`} className={`relative rounded-lg overflow-hidden h-24 border-2 ${i === (animal.foto_capa_index || 0) ? 'border-primary' : 'border-transparent'}`}>
                  <img src={resolveUrl(f)} alt="" className="w-full h-full object-cover cursor-pointer" onClick={() => setPhotoPopup(resolveUrl(f))} onError={e => { e.target.src = placeholderImg; }} />
                  {i === (animal.foto_capa_index || 0) && <Star className="absolute top-1 right-1 w-3 h-3 text-primary fill-primary" />}
                  <div className="absolute top-1 left-1 flex gap-0.5">
                    <button title="Ajustar foto" data-testid={`edit-foto-${i}`}
                      onClick={(e) => { e.stopPropagation(); startEditExistingPhoto(i); }}
                      className="bg-black/60 hover:bg-blue-600 text-white rounded-full p-1">
                      <Crop className="w-3 h-3" />
                    </button>
                    <button title="Apagar foto" data-testid={`delete-foto-${i}`}
                      onClick={async (e) => {
                        e.stopPropagation();
                        if (!window.confirm('Apagar esta foto?')) return;
                        try {
                          await axios.delete(`${API}/animais/${animal.id}/fotos/${i}`);
                          await refreshAnimal();
                          onUpdate?.();
                        } catch (err) { alert(err.response?.data?.detail || 'Erro a apagar foto'); }
                      }}
                      className="bg-black/60 hover:bg-destructive text-white rounded-full p-1">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                  <button data-testid={`set-cover-${i}`} onClick={async () => { await axios.put(`${API}/animais/${animal.id}/foto-capa`, { index: i }); await refreshAnimal(); onUpdate?.(); }}
                    className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[9px] text-center py-1 hover:bg-black/80 transition-colors cursor-pointer">
                    {i === (animal.foto_capa_index || 0) ? 'Capa atual' : 'Definir capa'}
                  </button>
                </div>
              ))}</div></div>
          </>)}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 p-4 border-t border-border">
          <div className="flex gap-2">
            {!animal.saida && (<>
              <button data-testid="exit-adocao" onClick={() => { setShowExit('adocao'); setExitData({ data: new Date().toISOString().slice(0, 10) }); }} className="text-xs bg-green-600 text-white rounded-full px-3 py-1.5 flex items-center gap-1"><Heart className="w-3 h-3" />Adoção</button>
              <button data-testid="exit-falecimento" onClick={() => { setShowExit('falecimento'); setExitData({ data: '' }); }} className="text-xs bg-gray-700 text-white rounded-full px-3 py-1.5 flex items-center gap-1"><AlertTriangle className="w-3 h-3" />Falecimento</button>
              <button data-testid="exit-levantamento" onClick={() => { setShowExit('levantamento'); setExitData({ data: new Date().toISOString().slice(0, 10) }); }} className="text-xs bg-blue-600 text-white rounded-full px-3 py-1.5">Levantamento</button>
            </>)}
            {animal.saida && animal.motivo_saida !== 'falecimento' && (
              <button data-testid="regresso-btn" onClick={() => { setShowRegresso(true); setRegressoData({ data_regresso: new Date().toISOString().slice(0, 10), motivo_volta: '' }); }}
                className="text-xs bg-amber-600 text-white rounded-full px-3 py-1.5 flex items-center gap-1">Retorno ao Canil</button>
            )}
          </div>
          <div className="flex gap-2">
            {canDelete && <button onClick={removeAnimal} data-testid="remove-animal" className="text-xs bg-destructive text-destructive-foreground rounded-full px-3 py-1.5 flex items-center gap-1"><Trash2 className="w-3 h-3" />Remover</button>}
            <button onClick={() => onEdit(animal.id)} data-testid="edit-animal" className="text-xs bg-primary text-primary-foreground rounded-full px-3 py-1.5 flex items-center gap-1"><Edit className="w-3 h-3" />Editar</button>
          </div>
        </div>

        {showDeleteModal && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-20" data-testid="delete-animal-modal">
            <div className="bg-card rounded-2xl border border-destructive/40 p-6 w-full max-w-sm m-4 space-y-3">
              <h3 className="font-heading font-bold text-lg text-destructive flex items-center gap-2"><Trash2 className="w-5 h-5" />Eliminar animal?</h3>
              <p className="text-sm text-foreground">
                Vais eliminar <strong>permanentemente</strong> a ficha de <strong>{animal.nome}</strong>, incluindo todo o histórico:
                vacinas, tratamentos, testes, desparasitações, fotos e ficheiros.
              </p>
              <p className="text-sm font-semibold text-destructive">Esta ação NÃO pode ser desfeita.</p>
              <div>
                <label className="text-xs font-medium block mb-1">Para confirmar, escreve <span className="font-bold">ELIMINAR</span>:</label>
                <input value={deleteText} onChange={e => setDeleteText(e.target.value)} data-testid="delete-confirm-input"
                  placeholder="ELIMINAR" autoFocus
                  className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background" />
              </div>
              <div className="flex gap-2 justify-end pt-1">
                <button onClick={() => setShowDeleteModal(false)} data-testid="delete-cancel-btn"
                  className="px-4 py-2 rounded-full bg-muted text-foreground text-sm">Cancelar</button>
                <button onClick={confirmRemoveAnimal} disabled={deleteText.trim().toUpperCase() !== 'ELIMINAR' || deleting} data-testid="delete-confirm-btn"
                  className="px-4 py-2 rounded-full bg-destructive text-destructive-foreground text-sm font-medium disabled:opacity-40">
                  {deleting ? 'A eliminar…' : 'Eliminar definitivamente'}
                </button>
              </div>
            </div>
          </div>
        )}

        {showExit && (
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center z-10">
            <div className="bg-card rounded-2xl border border-border p-6 w-full max-w-sm m-4 space-y-3">
              <h3 className="font-heading font-bold text-lg">Registar {showExit === 'adocao' ? 'Adoção' : showExit === 'falecimento' ? 'Falecimento' : 'Levantamento'}</h3>
              <div><label className="text-xs">Data</label><DateInput value={exitData.data || ''} onChange={v => setExitData({ ...exitData, data: v })} className="w-full" /></div>
              {showExit === 'adocao' && <div><label className="text-xs">Dados do Adotante</label><input value={exitData.dados_adotante || ''} onChange={e => setExitData({ ...exitData, dados_adotante: e.target.value })} placeholder="Nome, contacto..." className="w-full rounded-lg border border-border bg-background px-2 py-1.5 text-sm" /></div>}
              {showExit === 'falecimento' && (<><div><label className="text-xs">Tipo</label><select value={exitData.tipo_falecimento || ''} onChange={e => setExitData({ ...exitData, tipo_falecimento: e.target.value })} className="w-full rounded-lg border border-border bg-background px-2 py-1.5 text-sm"><option value="">Selecionar...</option>{DEATH_TYPES.map(d => <option key={d.v} value={d.v}>{d.l}</option>)}</select></div>
                <div><label className="text-xs">Observações</label><textarea rows={2} value={exitData.observacoes_falecimento || ''} onChange={e => setExitData({ ...exitData, observacoes_falecimento: e.target.value })} className="w-full rounded-lg border border-border bg-background px-2 py-1.5 text-sm" /></div></>)}
              {showExit === 'levantamento' && (<><div><label className="text-xs">Nome</label><input value={exitData.nome_levantamento || ''} onChange={e => setExitData({ ...exitData, nome_levantamento: e.target.value })} className="w-full rounded-lg border border-border bg-background px-2 py-1.5 text-sm" /></div>
                <div><label className="text-xs">Contacto</label><input value={exitData.contacto_levantamento || ''} onChange={e => setExitData({ ...exitData, contacto_levantamento: e.target.value })} className="w-full rounded-lg border border-border bg-background px-2 py-1.5 text-sm" /></div>
                <div><label className="text-xs">Tipo</label><select value={exitData.tipo_levantamento || ''} onChange={e => setExitData({ ...exitData, tipo_levantamento: e.target.value })} className="w-full rounded-lg border border-border bg-background px-2 py-1.5 text-sm"><option value="">Selecionar...</option><option value="levantamento_titular">Levantamento por Titular</option><option value="termino_sequestro">Término de Sequestro</option></select></div></>)}
              <div className="flex gap-2 justify-end pt-2">
                <button onClick={() => setShowExit(null)} className="text-xs bg-muted text-foreground rounded-full px-3 py-1.5">Cancelar</button>
                <button onClick={handleExit} data-testid="confirm-exit" className="text-xs bg-primary text-primary-foreground rounded-full px-3 py-1.5">Confirmar</button>
              </div>
            </div>
          </div>
        )}
        {showRegresso && (
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center z-10">
            <div className="bg-card rounded-2xl border border-border p-6 w-full max-w-sm m-4 space-y-3">
              <h3 className="font-heading font-bold text-lg">Retorno ao Canil</h3>
              <p className="text-sm text-muted-foreground">O animal <strong>{animal.nome}</strong> vai retornar ao canil.</p>
              <div><label className="text-xs">Data de Retorno</label><DateInput value={regressoData.data_regresso} onChange={v => setRegressoData({ ...regressoData, data_regresso: v })} className="w-full" /></div>
              <div><label className="text-xs">Motivo do Retorno</label><textarea rows={2} value={regressoData.motivo_volta} onChange={e => setRegressoData({ ...regressoData, motivo_volta: e.target.value })} placeholder="Ex: Adoção correu mal, animal devolvido..." className="w-full rounded-lg border border-border bg-background px-2 py-1.5 text-sm" /></div>
              <div className="flex gap-2 justify-end pt-2">
                <button onClick={() => setShowRegresso(false)} className="text-xs bg-muted text-foreground rounded-full px-3 py-1.5">Cancelar</button>
                <button onClick={handleRegresso} data-testid="confirm-regresso" className="text-xs bg-amber-600 text-white rounded-full px-3 py-1.5">Confirmar Retorno</button>
              </div>
            </div>
          </div>
        )}
        {photoPopup && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80" onClick={() => setPhotoPopup(null)}>
            <img src={photoPopup} alt="Foto" className="max-w-[90vw] max-h-[90vh] rounded-2xl shadow-2xl" onError={e => { e.target.src = placeholderImg; }} />
            <button onClick={() => setPhotoPopup(null)} className="absolute top-4 right-4 bg-white/20 text-white rounded-full p-2 hover:bg-white/40"><X className="w-6 h-6" /></button>
          </div>
        )}
        {cropperFiles && (
          <ImageCropper files={cropperFiles}
            onCancel={() => setCropperFiles(null)}
            onDone={uploadAdjustedPhotos} />
        )}
      </div>
    </div>
  );
}

function InfoRow({ label, value }) {
  return <p className="text-sm leading-relaxed"><span className="font-medium text-foreground">{label}:</span> <span className="text-muted-foreground">{value || 'N/D'}</span></p>;
}
